import { useState, useEffect } from "react";
import { useAuth } from "~/lib/auth-context";
import {
  useRecipeMetadataForRecipe,
  useRecipeMetadata,
} from "~/lib/recipe-metadata-context";
import { useNavigate } from "@remix-run/react";

interface FavoriteButtonProps {
  recipeId: string;
  className?: string;
}

export default function FavoriteButton({
  recipeId,
  className = "",
}: FavoriteButtonProps) {
  const { user, loading } = useAuth();
  const { updateFavorite } = useRecipeMetadata();
  const { isFavorite } = useRecipeMetadataForRecipe(recipeId);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // 즐겨찾기 토글 처리
  const handleToggleFavorite = async () => {
    // 로그인하지 않은 경우 로그인 페이지로 이동
    if (!user) {
      navigate(
        "/login?redirect=" + encodeURIComponent(window.location.pathname)
      );
      return;
    }

    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append("recipeId", recipeId);

      const response = await fetch("/api/favorites/toggle", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (response.status === 401) {
        // 인증 실패 시 로그인 페이지로 이동
        console.warn("인증이 만료되었습니다. 로그인 페이지로 이동합니다.");
        navigate(
          "/login?redirect=" + encodeURIComponent(window.location.pathname)
        );
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          updateFavorite(recipeId, data.isFavorite);
        }
      } else {
        console.error(
          "즐겨찾기 토글 실패:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("즐겨찾기 토글 오류:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={isLoading}
      className={`touch-target inline-flex items-center justify-center p-2 rounded-full transition-colors touch-manipulation ${
        isFavorite
          ? "text-red-500 hover:text-red-600 bg-red-50 dark:bg-red-50/60 hover:bg-red-100 dark:hover:bg-red-100/80 active:bg-red-200 dark:active:bg-red-200/80"
          : "text-gray-400 hover:text-red-500 bg-gray-50 dark:bg-gray-800/80 hover:bg-red-50 dark:hover:bg-red-900/60 active:bg-red-100 dark:active:bg-red-900/80"
      } ${className}`}
      aria-label={isFavorite ? "즐겨찾기 제거" : "즐겨찾기 추가"}
    >
      {isLoading ? (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ) : (
        <svg
          className="h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill={isFavorite ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth={isFavorite ? "0" : "1.5"}
        >
          <path
            fillRule="evenodd"
            d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button>
  );
}
